import { SerializedStyles } from "@emotion/react";
import { Ref, memo, useCallback, useState } from "react";
import RegionUnsupportedModal from "src/apps/AppFlow/pages/HomeLocation/RegionUnsupportedModal";
import { usePostalScoreQuery } from "src/apps/AppFlow/pages/HomeLocation/queries";
import { usePropertyValuation } from "src/apps/AppFlow/pages/hooks/usePropertyValuation";
import { AppFlowFirstScreenAgreement, FormFlowPage, LocationInput, TextInput } from "src/components";
import { useEffectOnce, useQueryParams, useRedirect } from "src/hooks";
import { selectApplicationLocation } from "src/selectors";

import { engine, enums, formatters, types } from "@fraction/shared";

import { useNoSubmit } from "../hooks";
import { styles } from "../styles";

export interface HomeLocationProps {
  value?: types.ExtractedPropertyAddress;
  onChange?: (location?: types.ExtractedPropertyAddress) => void;
  onNext: () => void;
  onBack?: () => void;
  style?: SerializedStyles;
  focusRef?: Ref<HTMLInputElement>;
  title?: string;
  description?: string;
  // a secondary property doesn't need to be in the correct location
  secondaryProperty?: boolean;
  showConsent?: boolean;
}

const HomeLocation = ({
  value,
  onChange,
  onNext,
  onBack,
  style,
  focusRef,
  title,
  description,
  secondaryProperty,
  showConsent,
  ...props
}: HomeLocationProps) => {
  const [regionAllowed, setRegionAllowed] = useState(true);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const { isCanada } = selectApplicationLocation(value);
  // only do postal score query for Canada
  const { isLoading, data: postalScoreResult } = usePostalScoreQuery(
    isCanada ? value?.postalCode : undefined
  );
  const { allowAnyRegion } = useQueryParams();
  const postalCodeNotAllowed = postalScoreResult?.postalScoreCheckStatus === enums.EngineRuleStatus.FAILED;

  const validate = useCallback(
    () =>
      !!value?.googlePlaceId &&
      (regionAllowed || !!secondaryProperty) &&
      (!showConsent || consentAgreed) &&
      !postalCodeNotAllowed &&
      !isLoading,
    [value, regionAllowed, secondaryProperty, showConsent, consentAgreed, postalCodeNotAllowed, isLoading]
  );
  const { handleNext, nextDisabled } = useNoSubmit({ onNext, validate });

  const handleBackToHome = useRedirect("https://www.fraction.com?waitlisted=true");

  const handleCloseUnsupportedRegionModal = useCallback(() => {
    // If they want to close the unsupported region modal, clear the location input
    onChange?.(undefined);
  }, [onChange]);

  const handleChangePremise = useCallback(
    (premise?: string) => {
      onChange?.({ ...value, premise });
    },
    [onChange, value]
  );

  // prepare the property valuation cache for the next page
  usePropertyValuation(value);

  const handleChangeLocation = useCallback(
    (location?: types.ExtractedPropertyAddress) => {
      onChange?.({ ...location, premise: location?.premise || value?.premise });

      if (secondaryProperty) {
        return;
      }

      const result = engine.subprocessors.adminAreaValidationSubProcessor(
        location?.administrativeArea,
        !location
      );

      if (!allowAnyRegion && result.status === enums.EngineRuleStatus.FAILED) {
        setRegionAllowed(false);
      } else {
        setRegionAllowed(true);
      }
    },
    [onChange, value?.premise, secondaryProperty, allowAnyRegion]
  );

  useEffectOnce(
    () => {
      handleChangeLocation(value);
    },
    [value, handleChangeLocation],
    !value?.googlePlaceId
  );

  return (
    <>
      <FormFlowPage
        {...props}
        title={title || "What's your address?"}
        description={description || "This helps us determine if Fraction is available in your area."}
        onNext={handleNext}
        onBack={onBack}
        style={style}
        nextDisabled={nextDisabled}
        nextLoading={isLoading}
        isStickyDesktopFooter
      >
        <LocationInput
          locationTypes={["address"]}
          ref={focusRef}
          name="propertyAddress"
          label="Property address"
          placeholder="1234 Electronic Ave, Vancouver, British Columbia, CA"
          style={styles.topInput}
          onSelect={handleChangeLocation}
          value={value ? formatters.property.formattedAddress(value, { premise: true }) : undefined}
        />
        <TextInput
          style={styles.input}
          label="Unit number (optional)"
          placeholder="5"
          value={value?.premise || ""}
          onChange={handleChangePremise}
        />
        {showConsent ? <AppFlowFirstScreenAgreement onChange={setConsentAgreed} /> : null}
      </FormFlowPage>
      <RegionUnsupportedModal
        onClose={handleCloseUnsupportedRegionModal}
        onComplete={handleBackToHome}
        open={!regionAllowed || postalCodeNotAllowed}
      />
    </>
  );
};

export default memo(HomeLocation);
